import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Plus } from 'lucide-react';
import { MusicIcon, VisualArtIcon, WritingIcon, VideoIcon, List } from '../../icons';
import { Card, Button } from '../../ui';
import { ProjectDto } from '../../../services';
import './ProgressOverview.css';

interface ProgressOverviewProps {
  opus: ProjectDto;
  pieces: ProjectDto[];
  isLoading?: boolean;
  onAddPiece?: () => void;
  onViewAllPieces?: () => void;
  onPieceClick?: (piece: ProjectDto) => void;
}

interface StageCount {
  stage: string;
  count: number;
}

interface DisciplineCount {
  type: string;
  count: number;
  icon: React.ReactNode;
}

const getDisciplineIcon = (type: string): React.ReactNode => {
  switch (type.toLowerCase()) {
    case 'music':
      return <MusicIcon size={16} />;
    case 'art':
      return <VisualArtIcon size={16} />;
    case 'writing':
      return <WritingIcon size={16} />;
    case 'video':
      return <VideoIcon size={16} />;
    default:
      return <MusicIcon size={16} />;
  }
};

const getStageBreakdown = (pieces: ProjectDto[]): StageCount[] => {
  const stageCounts: Record<string, number> = {};

  pieces.forEach((piece) => {
    const stage = piece.currentStage || 'Unknown';
    stageCounts[stage] = (stageCounts[stage] || 0) + 1;
  });

  return Object.entries(stageCounts)
    .map(([stage, count]) => ({ stage, count }))
    .sort((a, b) => b.count - a.count);
};

const getDisciplineBreakdown = (pieces: ProjectDto[]): DisciplineCount[] => {
  const disciplineCounts: Record<string, number> = {};

  pieces.forEach((piece) => {
    const type = piece.projectType || 'Unknown';
    disciplineCounts[type] = (disciplineCounts[type] || 0) + 1;
  });

  return Object.entries(disciplineCounts)
    .map(([type, count]) => ({
      type,
      count,
      icon: getDisciplineIcon(type),
    }))
    .sort((a, b) => b.count - a.count);
};

const getRecentActivity = (pieces: ProjectDto[], limit = 5): ProjectDto[] => {
  return [...pieces]
    .sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt).getTime();
      return dateB - dateA;
    })
    .slice(0, limit);
};

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const ProgressOverview: React.FC<ProgressOverviewProps> = ({
  opus,
  pieces,
  isLoading = false,
  onAddPiece,
  onViewAllPieces,
  onPieceClick,
}) => {
  const { t } = useTranslation();

  const completedPieces = pieces.filter((p) => p.status === 'Completed');
  const totalPieces = pieces.length;
  const stageBreakdown = getStageBreakdown(pieces);
  const disciplineBreakdown = getDisciplineBreakdown(pieces);
  const recentActivity = getRecentActivity(pieces);
  const completionRatio = totalPieces > 0 ? completedPieces.length / totalPieces : 0;

  if (isLoading) {
    return (
      <div className="progress-overview progress-overview--loading">
        <div className="progress-overview__skeleton" />
      </div>
    );
  }

  return (
    <div className="progress-overview">
      {/* Completion Ring Section */}
      <Card className="progress-overview__completion">
        <div className="progress-overview__completion-content">
          <div className="progress-overview__ring-container">
            <svg className="progress-overview__ring" viewBox="0 0 100 100">
              {/* Background circle */}
              <circle
                className="progress-overview__ring-bg"
                cx="50"
                cy="50"
                r="45"
                fill="none"
                strokeWidth="8"
              />
              {/* Progress arc */}
              <circle
                className="progress-overview__ring-progress"
                cx="50"
                cy="50"
                r="45"
                fill="none"
                strokeWidth="8"
                strokeDasharray={`${completionRatio * 283} 283`}
                strokeDashoffset="0"
                transform="rotate(-90 50 50)"
              />
            </svg>
            <div className="progress-overview__ring-text">
              <span className="progress-overview__ring-count">
                {completedPieces.length}/{totalPieces}
              </span>
              <span className="progress-overview__ring-label">
                {t('opus.dashboard.piecesComplete', 'Pieces')}
              </span>
            </div>
          </div>
          <div className="progress-overview__completion-message">
            {completedPieces.length === totalPieces && totalPieces > 0 ? (
              <div className="progress-overview__celebration">
                <CheckCircle size={20} />
                <span>{t('opus.dashboard.allComplete', 'All pieces complete!')}</span>
              </div>
            ) : (
              <span className="progress-overview__encouragement">
                {getEncouragementMessage(completedPieces.length, totalPieces)}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Pieces by Stage */}
      <Card className="progress-overview__stages">
        <h3 className="progress-overview__section-title">
          {t('opus.dashboard.byStage', 'Pieces by Stage')}
        </h3>
        {stageBreakdown.length > 0 ? (
          <div className="progress-overview__stage-list">
            {stageBreakdown.map(({ stage, count }) => (
              <div key={stage} className="progress-overview__stage-item">
                <span className="progress-overview__stage-name">{stage}</span>
                <span className="progress-overview__stage-count">{count}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="progress-overview__empty">
            {t('opus.dashboard.noPiecesYet', 'No pieces yet')}
          </p>
        )}
      </Card>

      {/* Discipline Breakdown */}
      <Card className="progress-overview__disciplines">
        <h3 className="progress-overview__section-title">
          {t('opus.dashboard.byDiscipline', 'By Discipline')}
        </h3>
        {disciplineBreakdown.length > 0 ? (
          <div className="progress-overview__discipline-list">
            {disciplineBreakdown.map(({ type, count, icon }) => (
              <div key={type} className="progress-overview__discipline-item">
                <span className="progress-overview__discipline-icon">{icon}</span>
                <span className="progress-overview__discipline-type">{type}</span>
                <span className="progress-overview__discipline-count">{count}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="progress-overview__empty">
            {t('opus.dashboard.noPiecesYet', 'No pieces yet')}
          </p>
        )}
      </Card>

      {/* Recent Activity */}
      <Card className="progress-overview__activity">
        <h3 className="progress-overview__section-title">
          {t('opus.dashboard.recentActivity', 'Recent Activity')}
        </h3>
        {recentActivity.length > 0 ? (
          <div className="progress-overview__activity-list">
            {recentActivity.map((piece) => (
              <button
                key={piece.id}
                className="progress-overview__activity-item"
                onClick={() => onPieceClick?.(piece)}
                type="button"
              >
                <span className="progress-overview__activity-icon">
                  {getDisciplineIcon(piece.projectType)}
                </span>
                <div className="progress-overview__activity-info">
                  <span className="progress-overview__activity-title">{piece.title}</span>
                  <span className="progress-overview__activity-stage">{piece.currentStage}</span>
                </div>
                <span className="progress-overview__activity-time">
                  {formatRelativeTime(piece.updatedAt || piece.createdAt)}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="progress-overview__empty">
            {t('opus.dashboard.noActivity', 'No recent activity')}
          </p>
        )}
      </Card>

      {/* Quick Actions */}
      <Card className="progress-overview__actions">
        <h3 className="progress-overview__section-title">
          {t('opus.dashboard.quickActions', 'Quick Actions')}
        </h3>
        <div className="progress-overview__action-buttons">
          <Button
            variant="primary"
            onClick={onAddPiece}
            className="progress-overview__action-btn"
          >
            <Plus size={16} />
            {t('opus.dashboard.addPiece', 'Add Piece')}
          </Button>
          {totalPieces > 0 && (
            <Button
              variant="secondary"
              onClick={onViewAllPieces}
              className="progress-overview__action-btn"
            >
              <List size={16} />
              {t('opus.dashboard.viewAll', 'View All Pieces')}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

// Helper for encouragement message - never shaming, always supportive
const getEncouragementMessage = (completed: number, total: number): string => {
  if (total === 0) return 'Ready to start creating';
  const ratio = completed / total;
  if (ratio === 0) return 'Ready to begin your creative journey';
  if (ratio < 0.25) return 'Great start! Every piece counts';
  if (ratio < 0.5) return 'Making progress - keep your rhythm';
  if (ratio < 0.75) return 'Over halfway there!';
  return 'Almost there - the finish line is in sight';
};

export default ProgressOverview;
